import { useEffect, useState } from 'react';
import { AlertTriangle, Box, Check, Info, Loader2, RotateCcw } from 'lucide-react';
import {
  apiService,
  PackBlockDTO,
  PackCellOffset,
  PackGeometryAnalysis,
  PackOversizedInput,
  PackOversizedMode,
} from '@/services/api.service';
import styles from './Oversized.module.scss';

/**
 * Gros blocs de déco (« objets » PropsiMod) : réglages d'un bloc dans l'éditeur.
 *
 * Le panel analyse la géométrie (GeometryBounds côté API) : au-delà de la limite de rendu d'un bloc
 * (30 px), le bloc devient un objet porté par une entité d'affichage, avec des remplissages
 * invisibles sur son empreinte. Visible seulement pour les groupes qui ont le drapeau oversizedBlocks.
 */

export type Footprint = 'cells' | 'display_only';

export interface OversizedDraft {
  mode: PackOversizedMode;
  /** null = proposition de l'analyse. */
  footprint: Footprint | null;
  /** null = cellules calculées au seuil ; sinon choix manuel (clics dans l'aperçu). */
  cells: PackCellOffset[] | null;
  passable: boolean;
  /** Seuil de remplissage (0..1). */
  threshold: number;
}

export const DEFAULT_THRESHOLD = 0.25;

export function initialOversizedDraft(block: PackBlockDTO): OversizedDraft {
  const o = block.oversized;
  if (!o) return { mode: 'auto', footprint: null, cells: null, passable: false, threshold: DEFAULT_THRESHOLD };
  return {
    // Un objet existant reste un objet tant qu'on ne choisit pas autre chose.
    mode: 'force',
    footprint: o.footprint === 'display_only' ? 'display_only' : 'cells',
    cells: o.footprint === 'display_only' ? null : o.cells,
    passable: o.passable,
    threshold: DEFAULT_THRESHOLD,
  };
}

/** Le bloc sera-t-il un objet après enregistrement ? (auto : seulement au-delà de la limite stricte) */
export function effectiveObject(d: OversizedDraft, a: PackGeometryAnalysis | null): boolean {
  if (d.mode === 'force') return true;
  if (d.mode === 'off') return false;
  return a?.strictVerdict === 'too_big';
}

export function effectiveFootprint(d: OversizedDraft, a: PackGeometryAnalysis | null): Footprint {
  return d.footprint ?? a?.proposedFootprint ?? 'cells';
}

export function effectiveCells(d: OversizedDraft, a: PackGeometryAnalysis | null): PackCellOffset[] {
  return d.cells ?? a?.footprint ?? [];
}

const cellKey = (c: PackCellOffset) => `${c.x},${c.y},${c.z}`;

/** Ajoute ou retire une cellule de l'empreinte (passe en choix manuel et en mode cellules). */
export function toggleDraftCell(d: OversizedDraft, a: PackGeometryAnalysis | null, c: PackCellOffset): OversizedDraft {
  const cur = effectiveCells(d, a);
  const has = cur.some((x) => cellKey(x) === cellKey(c));
  const next = has ? cur.filter((x) => cellKey(x) !== cellKey(c)) : [...cur, { x: c.x, y: c.y, z: c.z }];
  return { ...d, cells: next, footprint: 'cells' };
}

/** Réglage envoyé à l'enregistrement : ce qui est affiché est ce qui est écrit. */
export function toOversizedInput(d: OversizedDraft, a: PackGeometryAnalysis | null): PackOversizedInput {
  const footprint = effectiveFootprint(d, a);
  const cells = footprint === 'display_only' ? [] : d.cells ?? a?.footprint ?? null;
  return { mode: d.mode, footprint: d.footprint ?? a?.proposedFootprint ?? null, cells, passable: d.passable, threshold: d.threshold };
}

/** Analyse de la géométrie courante (pack ou fichier en attente), avec un léger différé. */
export function useGeometryAnalysis(
  groupId: string,
  enabled: boolean,
  geometryId: string,
  geometryText: string | null,
  threshold: number,
): { analysis: PackGeometryAnalysis | null; loading: boolean; error: string | null } {
  const [analysis, setAnalysis] = useState<PackGeometryAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!enabled || (!geometryText && !geometryId)) {
      setAnalysis(null);
      setError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const t = window.setTimeout(async () => {
      const r = geometryText
        ? await apiService.analyzePackGeometryText(groupId, geometryText, geometryId || undefined, threshold)
        : await apiService.analyzePackGeometry(groupId, geometryId, threshold);
      if (cancelled) return;
      setLoading(false);
      if (r.data) {
        setAnalysis(r.data);
        setError(null);
      } else {
        setAnalysis(null);
        setError(geometryText ? 'fichier de géométrie illisible' : `géométrie introuvable (${geometryId})`);
      }
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [groupId, enabled, geometryId, geometryText, threshold]);
  return { analysis, loading, error };
}

const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const px = (v: number) => (Math.round(v * 10) / 10).toString();

interface OversizedSettingsProps {
  block: PackBlockDTO;
  draft: OversizedDraft;
  onChange: (d: OversizedDraft) => void;
  analysis: PackGeometryAnalysis | null;
  loading: boolean;
  error: string | null;
  /** Géométrie choisie dans l'éditeur (vide = cube plein). */
  geometry: string;
  disabled?: boolean;
}

export function OversizedSettings({ block, draft, onChange, analysis, loading, error, geometry, disabled }: OversizedSettingsProps) {
  const object = effectiveObject(draft, analysis);
  const footprint = effectiveFootprint(draft, analysis);
  const cells = effectiveCells(draft, analysis);
  const set = (patch: Partial<OversizedDraft>) => onChange({ ...draft, ...patch });
  const current = block.oversized;

  return (
    <div className={styles.panel}>
      <div className={styles.panelTitle}>
        <Box size={14} /> Gros bloc de déco
        {current && <span className={styles.objectChip}>Objet</span>}
      </div>

      {current && (
        <div className={styles.current}>
          Objet en place : affiché par <code>{current.displayEntity}</code>,{' '}
          {current.footprint === 'display_only'
            ? 'affichage seul'
            : `${current.cells.length} remplissage(s)${current.passable ? ' traversables' : ''}`}
          .
        </div>
      )}

      {!geometry ? (
        <div className={`${styles.banner} ${styles.bannerOk}`}>
          <Check size={14} /> Pas de géométrie : cube plein, il tient dans le bloc.
        </div>
      ) : loading && !analysis ? (
        <div className={`${styles.banner} ${styles.bannerInfo}`}>
          <Loader2 size={14} className={styles.spin} /> Analyse de la géométrie…
        </div>
      ) : error ? (
        <div className={`${styles.banner} ${styles.bannerWarn}`}>
          <AlertTriangle size={14} /> Analyse impossible : {error}
        </div>
      ) : analysis ? (
        <VerdictBanner analysis={analysis} />
      ) : null}

      {analysis && !analysis.empty && (
        <div className={styles.dims}>
          Bornes x {px(analysis.min[0]!)} à {px(analysis.max[0]!)}, y {px(analysis.min[1]!)} à {px(analysis.max[1]!)}, z{' '}
          {px(analysis.min[2]!)} à {px(analysis.max[2]!)} px (repère JSON) · taille {px(analysis.size[0]!)} × {px(analysis.size[1]!)} ×{' '}
          {px(analysis.size[2]!)} px · limite 30 px
        </div>
      )}

      <div className={styles.row}>
        <span className={styles.rowLabel}>Mode</span>
        <div className={styles.segmented}>
          {([
            ['auto', 'Auto'],
            ['force', 'Forcer en objet'],
            ['off', 'Bloc classique'],
          ] as [PackOversizedMode, string][]).map(([m, label]) => (
            <button
              key={m}
              type="button"
              disabled={disabled}
              className={`${styles.seg} ${draft.mode === m ? styles.segActive : ''}`}
              onClick={() => set({ mode: m })}
            >
              {label}
            </button>
          ))}
        </div>
        <span className={object ? styles.resultObject : styles.resultBlock}>
          À l'enregistrement : {object ? 'objet' : 'bloc'}
        </span>
      </div>
      {draft.mode === 'auto' && (
        <div className={styles.hint}>Auto : objet seulement au-delà de la limite stricte du client (verdict « trop gros »).</div>
      )}

      <fieldset className={styles.objectFields} disabled={disabled || !object}>
        <div className={styles.row}>
          <span className={styles.rowLabel}>Empreinte</span>
          <div className={styles.segmented}>
            {([
              ['cells', 'Cellules'],
              ['display_only', 'Affichage seul'],
            ] as [Footprint, string][]).map(([f, label]) => (
              <button
                key={f}
                type="button"
                className={`${styles.seg} ${footprint === f ? styles.segActive : ''}`}
                onClick={() => set({ footprint: f })}
              >
                {label}
              </button>
            ))}
          </div>
          {draft.footprint === null && analysis && (
            <span className={styles.proposed}>proposé : {analysis.footprintReason}</span>
          )}
        </div>

        <div className={styles.row}>
          <span className={styles.rowLabel}>Seuil</span>
          <input
            className={styles.slider}
            type="range"
            min={5}
            max={100}
            step={5}
            value={Math.round(draft.threshold * 100)}
            disabled={footprint !== 'cells'}
            onChange={(e) => set({ threshold: Number(e.target.value) / 100, cells: null })}
          />
          <span className={styles.sliderValue}>{Math.round(draft.threshold * 100)} %</span>
          <span className={styles.proposed}>
            {footprint === 'cells' ? `${cells.length} cellule(s) retenue(s)` : 'aucun remplissage'}
          </span>
        </div>

        <div className={styles.row}>
          <span className={styles.rowLabel}>Collision</span>
          <div className={styles.segmented}>
            <button type="button" className={`${styles.seg} ${!draft.passable ? styles.segActive : ''}`} onClick={() => set({ passable: false })} disabled={footprint !== 'cells'}>
              Solide
            </button>
            <button type="button" className={`${styles.seg} ${draft.passable ? styles.segActive : ''}`} onClick={() => set({ passable: true })} disabled={footprint !== 'cells'}>
              Traversable
            </button>
          </div>
        </div>

        {footprint === 'cells' && (
          <div className={styles.hint}>
            {draft.cells ? 'Cellules choisies à la main. ' : 'Cellules remplies au seuil (hors base, pas sous la base). '}
            Clic sur une cellule de l'aperçu pour l'ajouter ou la retirer.
            {draft.cells && (
              <button type="button" className={styles.linkBtn} onClick={() => set({ cells: null })}>
                <RotateCcw size={12} /> Recalculer au seuil
              </button>
            )}
          </div>
        )}
      </fieldset>

      {analysis && analysis.warnings.length > 0 && (
        <ul className={styles.warnings}>
          {analysis.warnings.map((w) => <li key={w}>{w}</li>)}
        </ul>
      )}
    </div>
  );
}

function VerdictBanner({ analysis }: { analysis: PackGeometryAnalysis }) {
  // Trop gros d'après le calcul : c'est l'information décisive, même si un seuil est aussi frôlé.
  if (analysis.strictVerdict === 'too_big') {
    return (
      <div className={`${styles.banner} ${styles.bannerDanger}`}>
        <AlertTriangle size={14} />
        <div>
          <strong>Géométrie hors limites bloc ({analysis.reasons.join(', ')})</strong>
          <div className={styles.bannerSub}>
            Le client ne rend pas ce modèle en bloc : en mode Auto, il devient un objet à l'enregistrement.
            {analysis.verdict === 'limit' && ' Un autre seuil est frôlé par un cube tourné (voir ci-dessous).'}
          </div>
        </div>
      </div>
    );
  }
  switch (analysis.verdict) {
    case 'limit':
      return (
        <div className={`${styles.banner} ${styles.bannerWarn}`}>
          <AlertTriangle size={14} />
          <div>
            <strong>{cap(analysis.summary)}</strong>
            <div className={styles.bannerSub}>
              Seuil frôlé par un cube tourné : à contrôler en jeu. Auto laisse un bloc ; « Forcer en objet » si besoin.
            </div>
          </div>
        </div>
      );
    case 'overflow':
      return (
        <div className={`${styles.banner} ${styles.bannerInfo}`}>
          <Info size={14} />
          <div>
            <strong>{cap(analysis.summary)}</strong>
            <div className={styles.bannerSub}>« Forcer en objet » pour une empreinte à la taille du modèle.</div>
          </div>
        </div>
      );
    default:
      return (
        <div className={`${styles.banner} ${styles.bannerOk}`}>
          <Check size={14} /> {cap(analysis.summary)}
        </div>
      );
  }
}
